import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectHasStagedChanges,
  selectIsEditable,
  selectIsSaving,
  selectLatestStatus,
  selectModal,
  selectUsedInAnalyticalDatasets,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import { type PageHeaderAction } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useInlineProcessCohort } from './useInlineProcessCohort';
import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortValidation } from '../validation/useCohortValidation';

/**
 * Gen-3 action-button view-model. Replaces the legacy
 * `useCohortBuilderPageActions` (which was Redux + RTK Query coupled)
 * with a Zustand + TanStack version. AV-only: skips event-criteria
 * and event-name checks that don't apply to the v3 cohort model.
 *
 * Returns the same `PageHeaderAction[]` shape the gen-3 header
 * already renders, so the header itself doesn't change.
 *
 * Honors the `explicit_save` flag: when on, only Process is shown
 * (autosave handles persistence). When off, returns Undo + Save +
 * Process — same matrix as the legacy hook.
 *
 * Intentionally NOT carried over from the legacy hook:
 * - Event-criteria / event-name validation (legacy v2 only).
 */
export interface UseCohortDetailActionsResult {
  actions: PageHeaderAction[];
  // TODO: cleanup with skip_process_confirmation_modal ff
  processError: Error | null;
}

export const useCohortDetailActions = (): UseCohortDetailActionsResult => {
  const intl = useIntl();
  const { projectId, cohortId } = useParams();

  const status = useCohortDefinitionStore(selectLatestStatus);
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const isSaving = useCohortDefinitionStore(selectIsSaving);
  const hasLocalUnsavedChanges = useCohortDefinitionStore(selectHasStagedChanges);
  const modal = useCohortDefinitionStore(selectModal);
  const usedInAnalyticalDatasets = useCohortDefinitionStore(selectUsedInAnalyticalDatasets);
  const isWaitingForRA = useIsWaitingForResponse();

  const validation = useCohortValidation();
  const { openDialog, activeDialog } = useCohortBuilderModalContext();

  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  // TODO: cleanup with asset_edit_while_in_use ff
  const isAssetEditWhileInUseEnabled = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));
  // TODO: cleanup with skip_process_confirmation_modal ff
  const isInlineProcessEnabled = Boolean(useFeatureFlagSplit('skip_process_confirmation_modal'));
  const { runProcess, isProcessSubmitting, processError, isProcessReady } =
    useInlineProcessCohort();

  const isProcessing = status === CohortDefinitionStatus.PROCESSING;
  const isFinal = status === CohortDefinitionStatus.FINAL;
  const isBackendUnsaved = status === CohortDefinitionStatus.UNSAVED;
  // Only relevant with `asset_edit_while_in_use` on: a cohort is then editable
  // regardless of AD usage, but reprocessing it while a dependent AD is actively
  // PROCESSING would race with that AD's run reading the committed definition —
  // blocked server-side too (authoritative). With the flag off the cohort is
  // already read-only when used in an AD (via `selectIsEditable`), so this gate
  // stays inert.
  const hasProcessingDependentAd =
    isAssetEditWhileInUseEnabled &&
    usedInAnalyticalDatasets.some((ad) => ad.latestStatus === 'PROCESSING');

  const hasStagedChanges = hasLocalUnsavedChanges || isBackendUnsaved;

  const otherActivityOpen = modal !== undefined || activeDialog !== null;

  const baseDisabled =
    otherActivityOpen ||
    !validation.hasDslValue ||
    !isEditable ||
    isProcessing ||
    isFinal ||
    isSaving ||
    isWaitingForRA;

  const processDisabled =
    baseDisabled ||
    !validation.isCohortDefinitionValid ||
    hasProcessingDependentAd ||
    // TODO: cleanup with skip_process_confirmation_modal ff
    (isInlineProcessEnabled && (isProcessSubmitting || !isProcessReady));
  const saveDisabled = baseDisabled || !hasStagedChanges;
  const undoDisabled =
    !isEditable ||
    (!isBackendUnsaved && !hasLocalUnsavedChanges) ||
    isProcessing ||
    otherActivityOpen ||
    isSaving;

  const processTooltip = (() => {
    if (!isEditable) return undefined;
    if (!validation.hasDslValue) return undefined;
    if (isProcessing) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.processing',
      });
    }
    if (isWaitingForRA) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.raProcessing',
      });
    }
    if (hasProcessingDependentAd) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.processTooltip.dependentDatasetProcessing',
      });
    }
    if (validation.hasConceptGroupErrors) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.hasErrors',
      });
    }
    if (validation.hasDuplicateCriteria) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.processTooltip.duplicateCriteria',
      });
    }
    if (!validation.isCohortDefinitionValid) {
      const id = !validation.isIndexDateDefined
        ? 'projects.cohortBuilderPage.header.processTooltip.notFullyDefinedRestricted'
        : 'projects.cohortBuilderPage.header.processTooltip.notFullyDefined';
      return intl.formatMessage({ id });
    }
    return undefined;
  })();

  const saveTooltip = (() => {
    if (!isEditable) return undefined;
    if (!validation.hasDslValue) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.notDefined',
      });
    }
    if (isProcessing) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.processing',
      });
    }
    if (isWaitingForRA) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveProcessTooltip.raProcessing',
      });
    }
    if (!hasStagedChanges) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.saveTooltip.noUnsavedChanges',
      });
    }
    return undefined;
  })();

  const undoTooltip = (() => {
    if (!isEditable) return undefined;
    if (isProcessing) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.undoTooltip.processing',
      });
    }
    if (isSaving) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.undoTooltip.autoSaving',
      });
    }
    if (!isBackendUnsaved && !hasLocalUnsavedChanges) {
      return intl.formatMessage({
        id: 'projects.cohortBuilderPage.header.undoTooltip.noChanges',
      });
    }
    return undefined;
  })();

  const undoAction: PageHeaderAction = {
    id: 'pageHeaderActionUndo',
    label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.undoButton' }),
    btnType: 'secondary',
    tooltipText: undoTooltip,
    disabled: undoDisabled,
    onClickHandler: () => {
      if (projectId && cohortId && (isBackendUnsaved || hasLocalUnsavedChanges)) {
        openDialog(CohortBuilderActionDialogType.REVERT_COHORT_DEFINITION);
      }
    },
  };

  const saveAction: PageHeaderAction = {
    id: 'pageHeaderActionSave',
    label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.saveButton' }),
    btnType: 'secondary',
    tooltipText: saveTooltip,
    disabled: saveDisabled,
    onClickHandler: () => {
      if (projectId && cohortId && validation.hasDslValue) {
        openDialog(CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION);
      }
    },
  };

  const processAction: PageHeaderAction = {
    id: 'pageHeaderActionProcess',
    label:
      // TODO: cleanup with skip_process_confirmation_modal ff
      isInlineProcessEnabled && isProcessSubmitting
        ? intl.formatMessage({ id: 'projects.cohortBuilderPage.header.processButton.queuing' })
        : intl.formatMessage({ id: 'projects.cohortBuilderPage.header.processButton' }),
    btnType: 'primary',
    tooltipText: processTooltip,
    disabled: processDisabled,
    // TODO: cleanup with skip_process_confirmation_modal ff
    ...(isInlineProcessEnabled ? { waitButton: isProcessSubmitting } : {}),
    onClickHandler: () => {
      if (!validation.isCohortDefinitionValid) return;
      // TODO: cleanup with skip_process_confirmation_modal ff
      if (isInlineProcessEnabled) {
        runProcess();
        return;
      }
      openDialog(CohortBuilderActionDialogType.PROCESS_COHORT_DEFINITION);
    },
  };

  // Hide the action buttons entirely for non-owners. Disabled buttons
  // on someone else's cohort just add chrome without offering anything;
  // the read-only viewer should see a clean header.
  if (!isEditable) return { actions: [], processError: null };

  return {
    actions: isExplicitSave ? [processAction] : [undoAction, saveAction, processAction],
    processError: isInlineProcessEnabled ? processError : null,
  };
};
